import { useEffect, useState } from "react";
import { Storage } from "../../../Common/lib/storage";
import { isProjectOwnedByUser } from "../../../Common/lib/projectOwnership";
import { pdfjs } from "react-pdf";
import { MapContainer, TileLayer } from "react-leaflet";
import PdfViewer from "./PdfViewer";
import GPXDisplay from "../../DataVisualization/GPXVisualization/GPXDisplay";
import { FiPaperclip } from "react-icons/fi";
import { HiOutlineDownload } from "react-icons/hi";
import { MdImage, MdFileDownload } from 'react-icons/md';
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "leaflet/dist/leaflet.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const VisualizationPage = ({ printRef, project, isModerator, user }) => {
  const [visualizations, setVisualizations] = useState([]);
  const [visualizationsLoading, setVisualizationsLoading] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const isOwner = isProjectOwnedByUser(project, user);
  
    useEffect(() => {
      const loadVisualizations = async () => {
        setIsLoading(true); // Start loading
        try {
          const dataVisualization = JSON.parse(project.dataVisualization);
          const visualizationPromises = dataVisualization.map(async (visualization) => {
            const url = await Storage.get(visualization.s3Link);
            return { ...visualization, url };
          });
  
          // Wait for all visualizations to be fetched
          const loadedVisualizations = await Promise.all(visualizationPromises);
          setVisualizations(loadedVisualizations);
        } catch (error) {
          console.error('Error loading visualizations:', error);
        }
        setIsLoading(false); // End loading
      };
  
      if (project.dataVisualization) {
        loadVisualizations();
      }
    }, [project.dataVisualization]);
  
  const visibleVisualizations = visualizations.filter((visualization) =>
    (visualization.approved && !visualization.archived) || isModerator || isOwner
  );


  /**
   * Used to display the correct status pill (archived, under review) for a given visualization, if there is one.
   *
   * @param {*} visualization The visualization item.
   * @returns  Component
   */
  const getStatusPill = (visualization) => {
    if (visualization.archived) {
      return (
        <div
          data-cy={"pill-" + visualization.s3Link}
          className="whitespace-nowrap h-6 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 mb-1.5"
        >
          Archived
        </div>
      );
    } else if (!visualization.approved) {
      return (
        <div
          data-cy={"pill-" + visualization.s3Link}
          className="whitespace-nowrap h-6 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 mb-1.5 dark:bg-yellow-300 dark:text-yellow-900"
        >
          Under Review
        </div>
      );
    }
  };

  /**
   * Generates placeholder components to be displayed while the actual visualization content is loading.
   *
   * @returns {Array} An array of JSX elements, each representing a placeholder for a visualization item.
   */
  const renderPlaceholders = () => {
    return [...Array(5)].map((_, index) => (
      <div key={index} className="animate-pulse mb-8 last:mb-0">
        <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6">
          <div className="bg-gradient-to-r from-gray-300 to-gray-200 h-60 w-full md:w-1/2 flex items-center justify-center rounded-lg shadow-lg">
            <MdImage className="text-gray-500 text-6xl" />
          </div>
          <div className="w-full md:w-1/2 space-y-3">
            <div className="space-y-2">
              <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-5/6"></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-2/3"></div>
            </div>
            <div className="flex items-center pt-4 md:pt-0">
              <div className="h-10 bg-gray-200 rounded w-24 flex justify-center items-center">
                <MdFileDownload className="text-gray-500 text-3xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    ));
  };
  

  return (
    <>
      <div ref={printRef} className="max-w-5xl mx-auto">
        <div className="relative bg-white border shadow-sm border-gray-200 overflow-hidden rounded-lg pt-0 mt-0 dark:bg-gray-800 dark:border-gray-700">
          <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-blue-500 to-blue-600"></span>{" "}
          {/* The blue strip on the top */}
          <div className="px-4 py-5 sm:px-6">
          {isLoading ? renderPlaceholders() : (
            visibleVisualizations.length ? visibleVisualizations.map((visualization, index) => {
              return (
                  <>
                    <div className="flex flex-col-reverse lg:flex-row justify-between my-4">
                      <div className="basis-[50%] shrink-0">
                        {visualization.type === "image" && (
                          <img
                            src={visualization.url}
                            alt={visualization.title}
                            className="w-full max-h-full hover:cursor-pointer border-solid border-gray-200 border dark:border-gray-700"
                          />
                        )}
                        {visualization.type === "video" && (
                          <video className="w-full hover:cursor-pointer" controls>
                            <source src={visualization.url} />
                          </video>
                        )}
                        {visualization.type.includes("track") && (
                          <div style={{ height: "300px", width: "100%", position: "relative" }}>
                            {!visualizationsLoading[index] && (
                              <div className="absolute z-10" style={{ height: "100%", width: "100%" }}>
                                <div
                                  role="status"
                                  className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2"
                                >
                                  <svg
                                    aria-hidden="true"
                                    className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-white fill-blue-600"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                      fill="currentColor"
                                    />
                                    <path
                                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                      fill="currentFill"
                                    />
                                  </svg>
                                  <span className="sr-only">Loading...</span>
                                </div>
                              </div>
                            )}
                            <div
                              className={`transition ease-in-out duration-150 border-solid border-gray-200 border dark:border-gray-700 ${
                                !visualizationsLoading[index] ? "blur" : "blur-none"
                              }`}
                            >
                              <MapContainer
                                style={{
                                  height: "300px",
                                  width: "100%",
                                  zIndex: 0,
                                }}
                                center={[69.11868280984892, -105.04560470581056]}
                                zoom={2}
                                maxZoom={15}
                                attributionControl={false}
                                worldCopyJump={true}
                                maxBoundsViscosity={1}
                                minZoom={2}
                              >
                                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}" />
                                <GPXDisplay
                                  type={visualization.type}
                                  file={visualization.s3Link}
                                  index={index}
                                  setVisualizationsLoading={setVisualizationsLoading}
                                />
                              </MapContainer>
                            </div>
                          </div>
                        )}
                        {visualization.type === "pdf" && (
                          <div className="h-72 overflow-auto border-solid border-gray-200 border hover:cursor-pointer dark:border-gray-700">
                            <PdfViewer url={visualization.url} />
                          </div>
                        )}

                        <div
                          key={visualization.s3Link}
                          className="pl-3 pr-4 py-3 mt-4 flex justify-between items-center text-sm border border-gray-200 rounded-md shadow-sm dark:border-gray-700"
                        >
                          <div className="flex-1 flex items-center">
                            <FiPaperclip className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" />
                            <div className="ml-2 flex-1 w-0">
                              <p className="truncate font-medium dark:text-white">{visualization.s3Link.split("/")[1]}</p>
                            </div>
                          </div>
                          <div className="ml-4 flex-shrink-0 inline-flex items-center">
                            <span className="mr-1.5 text-gray-600 text-xs text-right dark:text-gray-400">
                              {visualization?.size}
                            </span>
                            <a
                              // onClick={() => {window.location.href = file.downloadLink}} // Can also use href attribute on the <a> tag. Hides download source when you inspect
                              href={visualization.url}
                              data-cy="download"
                              className="font-medium text-blue-600 hover:text-blue-500 p-0.5 hover:bg-gray-100 rounded-md mx-auto dark:hover:bg-gray-700"
                            >
                              <HiOutlineDownload className="h-6 w-6" aria-hidden="true" />
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="basis-[45%] grow-0 overflow-hidden mb-8 lg:mb-0">
                        <div className="flex justify-between border-solid border-gray-200 border-b dark:border-gray-700">
                          <span className="text-xl font-medium text-gray-900 leading-8 line-clamp-2 break-all dark:text-white">
                            {visualization.title}
                          </span>
                          <div className="my-auto">{getStatusPill(visualization)}</div>
                        </div>
                        <p className="mt-3 text-gray-700 whitespace-normal overflow-wrap break-words dark:text-gray-400">
                          {visualization.desc}
                        </p>
                      </div>
                    </div>

                    {index + 1 !== visibleVisualizations.length && (
                      <hr className="w-full my-6 md:my-12 lg:my-12 h-px border-t border-solid border-gray-200 dark:border-gray-700" />
                    )}
                  </>
                );
            }) : (
              <div className="py-12 text-center text-gray-600 dark:text-gray-300">
                No visualizations are available for this project yet. Uploads will appear here once approved.
              </div>
            )
          )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VisualizationPage;